import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { heartCountText } from "../../utils/heart_timer.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UICanvasRect } from "../common_ui_elements/ui_canvas_rect.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { InviteFriendDialog } from "./invite_friend_dialog.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { purchaseItem } from "../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer } from "../../api/player_sync.js";
import { OfflineEconomy } from "../../offline/offline_economy.js";
import { heartTimerText } from "../../utils/heart_timer.js";
import {
  logVirtualCurrencyEarned,
  logVirtualCurrencySpent
} from "../../analytics/appsflyer_events.js";
import { ApiError } from "../../api/http_client.js";
import { TransformRotate } from "../../transforms/transform_rotate.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { ITransformParams } from "../../types/transform_params.js";
import { Vector2 } from "../../common/vector2.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";

class SteppedRotate extends TransformRotate<UIImage>
{
  constructor(
    private readonly steps: number,
    target: UIImage,
    is_important: boolean,
    from: number | null,
    dest: number,
    anim_start_time: number,
    duration: number,
    optional_params: ITransformParams = {}
  )
  {
    super( target, is_important, from, dest, anim_start_time, duration, optional_params );
  }

  protected onUpdateTransform( eased: number ): void
  {
    super.onUpdateTransform( Math.floor( eased * this.steps ) / this.steps );
  }
}

export class MoreLivesDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 900;
  private static readonly DESIGN_H = 1460;
  private static readonly HEADER_W = 692;
  private static readonly HEADER_H = 257;
  private static readonly HEADER_Y = -15;
  private static readonly INFO_FRAME_W = 780;
  private static readonly INFO_FRAME_Y = 260;
  private static readonly BTN_W = 700;
  private static readonly BTN_H = 280;
  private static readonly ASK_FRIEND_BTN_Y = 820;
  private static readonly BUY_BTN_Y = 1120;

  private purchase_inflight = false;
  private buy_conflict_locked = false;
  private time_text: UITextElement | null = null;
  private heart_icon: UIImage | null = null;
  private heart_count_text: UITextElement | null = null;
  private buy_btn: UIImage | null = null;
  private close_btn!: UIImage;
  private buy_text: UITextElement | null = null;
  private buy_active = true;
  private price_text: UITextElement | null = null;
  private shown_timer = "";
  private shown_hearts = -1;
  private shown_hearts_infinite = false;
  private shown_price = -1;
  private next_sync_at = 0;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( MoreLivesDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( MoreLivesDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createHeader();
    this.createInfoFrame();
    this.createHeartsBadge();
    this.createAskFriendButton();
    this.createBuyButton();
    this.createCloseButton();
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  // Same header plate as EditNameDialog.
  private createHeader(): void
  {
    const design_title_w = 560;
    const design_title_y = 10;
    const design_title_h = 175;
    const design_font_size = 72;
    const design_outline_w = 10;

    const header_w = LayoutScaler.widthFromDesign( MoreLivesDialog.HEADER_W );
    const header = new UIImage(
      this.ctx,
      ( this.rect.getW() - header_w ) / 2,
      LayoutScaler.yFromDesign( MoreLivesDialog.HEADER_Y ),
      header_w,
      LayoutScaler.heightFromDesign( MoreLivesDialog.HEADER_H ),
      ImgID.UI_BoosterNameBg
    );
    this.addChild( header );

    const title_w = LayoutScaler.widthFromDesign( design_title_w );
    const title = new UITextElement(
      this.ctx,
      ( this.rect.getW() - title_w ) / 2,
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "MORE_LIVES_DIALOG.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        shadow_blur: LayoutScaler.widthFromDesign( 5 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        auto_wrap: true,
      }
    );
    title.setClickable( false );
    this.addChild( title );
  }

  private createInfoFrame(): void
  {
    const design_frame_h = 480;
    const design_frame_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.INFO_FRAME_W ) / 2;

    const frame_w = LayoutScaler.widthFromDesign( MoreLivesDialog.INFO_FRAME_W );
    const frame_h = LayoutScaler.heightFromDesign( design_frame_h );
    const frame_x = LayoutScaler.xFromDesign( design_frame_x );
    const frame_y = LayoutScaler.yFromDesign( MoreLivesDialog.INFO_FRAME_Y );

    const frame = new UIImage( this.ctx, frame_x, frame_y, frame_w, frame_h, ImgID.UI_PaternedBg );
    this.addChild( frame );

    const design_text_y = 240;
    const design_text_h = 70;
    const design_text_font_size = 60;
    const design_text_outline_w = 14;
    const design_text_outline_offset_y = 2;

    const MAGIC_NUM_CORRECTION = 0.8;
    const text_w = frame_w * MAGIC_NUM_CORRECTION;
    const text_h = LayoutScaler.heightFromDesign( design_text_h );

    const text = new UITextElement(
      this.ctx,
      ( frame_w - text_w ) / 2,
      LayoutScaler.yFromDesign( design_text_y ),
      text_w,
      text_h,
      {
        localization_key: "MORE_LIVES_DIALOG.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
        font_weight: 900,
        text_color: ColorScheme.hearts_text,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    frame.addChild( text );

    this.createCounterWithClock( frame );
  }

  private createCounterWithClock( frame: UIImage ): void
  {
    const design_clock_size = 90;
    const design_bg_w = 505;
    const design_bg_h = 105;
    const design_gap = 15;

    const design_group_w = design_bg_w + design_gap;
    const design_clock_x = ( MoreLivesDialog.INFO_FRAME_W - design_group_w ) / 2;
    const design_bg_x = design_clock_x - design_gap;
    const design_clock_y = 330;
    const design_bg_y = design_clock_y + ( design_clock_size - design_bg_h ) / 2;

    const clock_size = LayoutScaler.widthFromDesign( design_clock_size );
    const bg_w = LayoutScaler.widthFromDesign( design_bg_w );
    const bg_h = LayoutScaler.heightFromDesign( design_bg_h );

    const bg = new RoundedRectangle(
      this.ctx,
      LayoutScaler.xFromDesign( design_bg_x ),
      LayoutScaler.yFromDesign( design_bg_y ),
      bg_w,
      bg_h,
      LayoutScaler.cornerRadiusFromDesign( 53 ),
      ColorScheme.hearts_timer_bg
    );
    frame.addChild( bg );

    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_clock_x ),
      LayoutScaler.yFromDesign( design_clock_y ),
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    frame.addChild( clock );

    this.shown_timer = heartTimerText( StatisticsStorage.load(), Date.now() );

    const design_time_text_x = 210;
    const design_time_text_w = 200;

    const time_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_time_text_x ),
      0,
      LayoutScaler.widthFromDesign( design_time_text_w ),
      bg_h,
      {
        text: this.shown_timer,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        font_weight: 900,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.hearts_text,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
      }
    );
    bg.addChild( time_text );
    this.time_text = time_text;
  }

  private createHeartsBadge(): void
  {
    const design_outer_size = 216;
    const design_heart_size = 240;
    const design_badge_y_offset = 10;
    const design_heart_y_offset = 7;

    const design_center_x = MoreLivesDialog.DESIGN_W / 2;
    const design_outer_y = MoreLivesDialog.INFO_FRAME_Y - design_badge_y_offset;

    const design_heart_x = design_center_x - design_heart_size / 2;
    const design_heart_y = design_outer_y + ( design_outer_size - design_heart_size ) / 2 + design_heart_y_offset;

    const heart_w = LayoutScaler.widthFromDesign( design_heart_size );
    const heart_h = LayoutScaler.heightFromDesign( design_heart_size );

    const stats = StatisticsStorage.load();
    const hearts_infinite = stats.infinite_hearts_until > Date.now();
    const heart_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_heart_x ),
      LayoutScaler.yFromDesign( design_heart_y ),
      heart_w,
      heart_h,
      hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart
    );
    this.addChild( heart_icon );
    this.heart_icon = heart_icon;

    const design_count_text_y = -7;
    const design_count_font_size = 70;
    const design_count_shadow_blur = 6;
    const design_count_shadow_offset_y = 3;

    const heart_count_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_count_text_y ),
      heart_w,
      heart_h,
      {
        text: hearts_infinite ? "" : heartCountText( stats, Date.now() ),
        font_size: LayoutScaler.textSizeFromDesign( design_count_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        shadow_blur: LayoutScaler.widthFromDesign( design_count_shadow_blur ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( design_count_shadow_offset_y ),
      }
    );
    heart_icon.addChild( heart_count_text, true );
    this.heart_count_text = heart_count_text;
    this.shown_hearts = stats.hearts;
    this.shown_hearts_infinite = hearts_infinite;
  }

  private createAskFriendButton(): void
  {
    const design_btn_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.BTN_W ) / 2;
    const design_icon_size = 136;
    const design_icon_x = 85;
    const design_text_x = 200;
    const design_text_w = 400;
    const design_font_size = 50;
    const design_plus_font_size = 50;

    const btn_w = LayoutScaler.widthFromDesign( MoreLivesDialog.BTN_W );
    const btn_h = LayoutScaler.heightFromDesign( MoreLivesDialog.BTN_H );

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( MoreLivesDialog.ASK_FRIEND_BTN_Y ),
      btn_w,
      btn_h,
      ImgID.UI_OrangeBtn,
      undefined,
      true
    );

    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_icon_x ),
      ( btn_h - icon_size ) / 2,
      icon_size,
      icon_size,
      ImgID.IconHeart
    );
    btn.addChild( icon, true );

    const plus_one = new UITextElement(
      this.ctx, 0, -LayoutScaler.yFromDesign( 7 ), icon_size, icon_size,
      {
        text: "+1",
        font_size: LayoutScaler.textSizeFromDesign( design_plus_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    icon.addChild( plus_one, true );

    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_text_x ),
      LayoutScaler.yFromDesign( 10 ),
      LayoutScaler.widthFromDesign( design_text_w ),
      btn_h,
      {
        localization_key: "MORE_LIVES_DIALOG.ASK_FRIEND",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        outline_color: ColorScheme.hearts_ask_button,
        outline_width: LayoutScaler.widthFromDesign( 12 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    btn.addChild( text, true );

    btn.setOnClick( () =>
    {
      DialogManager.closeAllDialogs();

      if ( ScreenManager.getCurrentScreen() !== Screens.FRIENDS )
        void ScreenManager.showScreen( Screens.FRIENDS );
    } );

    this.addChild( btn );
  }

  private createBuyButton(): void
  {
    const design_btn_y = MoreLivesDialog.BUY_BTN_Y;
    const design_btn_x = ( MoreLivesDialog.DESIGN_W - MoreLivesDialog.BTN_W ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( MoreLivesDialog.BTN_W );
    const btn_h = LayoutScaler.heightFromDesign( MoreLivesDialog.BTN_H );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    const design_font_size = 60;

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.menu_button_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      ...TextStyleScheme.buttonShadow(),
    };

    const buy_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "BUY",
        ...text_options,
      }
    );

    const design_icon_size = 104;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx, 0, 0, icon_size, icon_size, ImgID.IconCoin
    );

    const stats = StatisticsStorage.load();
    this.shown_price = stats.heart_refill_price;

    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        text: String( this.shown_price ),
        ...text_options,
      }
    );
    this.buy_text = buy_text;
    this.price_text = price_text;

    const design_content_gap = 0;

    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, 0, 0, btn_w, btn_h,
      [ buy_text, icon, price_text ],
      LayoutScaler.widthFromDesign( design_content_gap )
    );
    btn.addChild( content, true );

    const hearts_full = stats.hearts >= stats.max_hearts;

    btn.setOnClick( () =>
    {
      if ( this.purchase_inflight || this.buy_conflict_locked )
        return;

      const current_stats = StatisticsStorage.load();
      if ( current_stats.hearts >= current_stats.max_hearts )
        return;

      if ( current_stats.coins < current_stats.heart_refill_price )
      {
        DialogManager.openDialog( new InviteFriendDialog() );
        return;
      }

      this.purchase_inflight = true;
      this.setBuyButtonActive( false );

      purchaseItem( "hearts_refill" )
        .then( r =>
        {
          const hearts_before = StatisticsStorage.load().hearts;
          adoptEconomyFromPlayer( r.player );
          const hearts_earned = Math.max( 0, StatisticsStorage.load().hearts - hearts_before );
          void logVirtualCurrencySpent( {
            currency_name: 'coins',
            amount: r.purchased.price,
            sink: 'hearts_refill',
          } );
          void logVirtualCurrencyEarned( {
            currency_name: 'hearts',
            amount: hearts_earned,
            source: 'hearts_refill',
          } );
          DialogManager.closeDialog();
        } )
        .catch( e =>
        {
          console.warn( '[Economy] hearts refill purchase failed', e );
          this.purchase_inflight = false;
          this.setBuyButtonActive( true );
          if ( e instanceof ApiError && e.status === 409 )
          {
            this.buy_conflict_locked = true;
            btn.setClickable( false );
            this.setBuyButtonActive( false );
          }
        } );
    } );

    this.addChild( btn );
    this.buy_btn = btn;

    if ( hearts_full )
    {
      btn.setClickable( false );
      this.setBuyButtonActive( false );
    }
  }

  private setBuyButtonActive( active: boolean ): void
  {
    if ( active === this.buy_active )
      return;

    this.buy_active = active;
    this.buy_btn?.setImageId( active ? ImgID.UI_GreenFrameBtn : ImgID.UI_GrayBtn );
    setButtonTextOutline( [ this.buy_text, this.price_text ], active, ColorScheme.menu_button_outline );
  }

  public override update( now: number ): void
  {
    super.update( now );

    if ( now < this.next_sync_at )
      return;

    this.next_sync_at = now + 1000;
    this.syncHeartsState();
  }

  private syncHeartsState(): void
  {
    const stats = StatisticsStorage.load();
    const now_ms = Date.now();

    const timer = heartTimerText( stats, now_ms );
    if ( timer !== this.shown_timer )
    {
      this.shown_timer = timer;
      this.time_text?.setText( timer );
    }

    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    if ( stats.hearts !== this.shown_hearts || hearts_infinite !== this.shown_hearts_infinite )
    {
      this.shown_hearts = stats.hearts;
      this.shown_hearts_infinite = hearts_infinite;
      this.heart_icon?.setImageId( hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart );
      this.heart_count_text?.setText( hearts_infinite ? "" : heartCountText( stats, now_ms ) );
    }

    const price = stats.heart_refill_price;
    if ( price !== this.shown_price )
    {
      this.shown_price = price;
      this.price_text?.setText( String( price ) );
    }

    const hearts_full = stats.hearts >= stats.max_hearts;

    if ( this.buy_btn && !this.purchase_inflight && !this.buy_conflict_locked )
    {
      this.buy_btn.setClickable( !hearts_full );
      this.setBuyButtonActive( !hearts_full );
    }

    if ( stats.hearts < stats.max_hearts
      && stats.next_heart_at > 0
      && now_ms >= stats.next_heart_at )
      OfflineEconomy.applyHeartsRegen( now_ms );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_btn_y = -40;
    const design_btn_x = MoreLivesDialog.DESIGN_W - design_btn_size + 25;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => DialogManager.closeDialog() );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public name(): string
  {
    return "MoreLivesDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
